import { useState } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CalendarDays, ChevronDown } from "lucide-react";
import { Arrow } from "@/components/icons";
import { routeHead } from "@/components/seo";
import { articleSchema, breadcrumbSchema, faqSchema } from "@/lib/schema";
import { site } from "@/content";
import { articles, getArticle, type Article, type ArticleSection } from "@/content/insights";

export const Route = createFileRoute("/insights/$slug")({
  loader: ({ params }) => {
    const article = getArticle(params.slug);
    if (!article) throw notFound();
    return { article };
  },
  head: ({ params }) => {
    const article = getArticle(params.slug);
    if (!article) return {};
    const url = `/insights/${article.slug}`;
    return routeHead(url, {
      title: article.metaTitle,
      description: article.description,
      jsonLd: [
        articleSchema({
          title: article.title,
          description: article.description,
          url,
          datePublished: article.date,
          keywords: article.keywords,
        }),
        faqSchema(article.faq),
        breadcrumbSchema([
          { name: "Home", item: "/" },
          { name: "Insights", item: "/insights" },
          { name: article.title, item: url },
        ]),
      ],
    });
  },
  component: Page,
});

function formatDate(iso: string) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function Section({ s }: { s: ArticleSection }) {
  switch (s.type) {
    case "h2":
      return (
        <h2 className="mt-12 font-serif text-2xl text-[color:var(--navy)] sm:text-3xl">{s.text}</h2>
      );
    case "h3":
      return <h3 className="mt-8 font-serif text-xl text-[color:var(--navy)]">{s.text}</h3>;
    case "p":
      return <p className="mt-5 leading-relaxed text-[color:var(--ink)]">{s.text}</p>;
    case "ul":
      return (
        <ul className="mt-5 space-y-2.5 pl-1">
          {s.items.map((item) => (
            <li
              key={item}
              className="flex items-start gap-3 leading-relaxed text-[color:var(--ink)]"
            >
              <span
                className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--brand)]"
                aria-hidden
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "callout":
      return (
        <aside className="mt-8 rounded-xl border-l-4 border-[color:var(--brand)] bg-[color:var(--sky)] p-6">
          <p className="font-serif text-lg text-[color:var(--navy)]">{s.title}</p>
          <p className="mt-2 text-[color:var(--ink)]">{s.text}</p>
        </aside>
      );
    case "table":
      return (
        <div className="mt-6 overflow-x-auto rounded-xl border border-[color:var(--mist)]">
          <table className="w-full min-w-[560px] border-collapse bg-white text-left text-sm">
            <thead>
              <tr className="bg-[color:var(--sky)]">
                {s.headers.map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold text-[color:var(--navy)]">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {s.rows.map((row) => (
                <tr key={row[0]} className="border-t border-[color:var(--mist)]">
                  {row.map((cell, i) => (
                    <td key={i} className="px-4 py-3 align-top text-[color:var(--ink)]">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

function MidArticleCta() {
  return (
    <aside className="horizon-field mt-12 overflow-hidden p-8 text-white">
      <p className="font-serif text-xl sm:text-2xl">Want this handled instead of managed?</p>
      <p className="mt-2 max-w-xl text-white/80">
        A Strategic Finance Review with Jamee, CPA looks at your books, payroll and reporting and
        maps the right level of support. Focused, practical, no pressure.
      </p>
      <div className="mt-5 flex flex-wrap gap-3">
        <Link to={site.primaryCta.href} className="btn-on-dark !px-5 !py-2.5">
          {site.primaryCta.label}
          <Arrow className="h-4 w-4" />
        </Link>
        <a
          href={site.contact.bookingUrl}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost-dark !px-5 !py-2.5"
        >
          <CalendarDays className="h-4 w-4" /> Book a 30 minute chat
        </a>
      </div>
    </aside>
  );
}

function Faq({ faqs }: { faqs: Article["faq"] }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="mt-14">
      <h2 className="font-serif text-2xl text-[color:var(--navy)]">Quick answers</h2>
      <div className="mt-6 divide-y divide-[color:var(--mist)] border-y border-[color:var(--mist)]">
        {faqs.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={f.question}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-6 py-4 text-left"
              >
                <span className="font-medium text-[color:var(--navy)]">{f.question}</span>
                <ChevronDown
                  className={`h-5 w-5 shrink-0 text-[color:var(--brand)] transition-transform ${isOpen ? "rotate-180" : ""}`}
                  aria-hidden
                />
              </button>
              {isOpen && (
                <p className="pb-5 pr-10 leading-relaxed text-[color:var(--ink)]">{f.answer}</p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Page() {
  const { article } = Route.useLoaderData();
  const related = article.related
    .map((slug) => articles.find((a) => a.slug === slug))
    .filter((a): a is Article => !!a);

  // Place the conversion block roughly two thirds of the way through the body.
  const ctaIndex = Math.max(1, Math.floor(article.sections.length * 0.66));

  return (
    <div className="bg-background text-foreground">
      <div className="reading-progress" aria-hidden />
      <article>
        <header className="border-b border-[color:var(--mist)] bg-white">
          <div className="mx-auto max-w-3xl px-6 pb-12 pt-14">
            <nav aria-label="Breadcrumb" className="text-xs text-[color:var(--subtle)]">
              <Link to="/insights" className="hover:text-[color:var(--brand)]">
                Insights
              </Link>
              <span aria-hidden> / </span>
              <span>{article.category}</span>
            </nav>
            <h1 className="mt-4 font-serif text-3xl leading-[1.15] text-[color:var(--navy)] sm:text-4xl lg:text-[2.75rem]">
              {article.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[color:var(--subtle)]">
              <span className="inline-flex items-center gap-2">
                <img
                  src="/images/jamee.webp"
                  alt=""
                  width="28"
                  height="28"
                  className="h-7 w-7 rounded-full object-cover"
                  loading="lazy"
                />
                Jamee White, CPA
              </span>
              <span aria-hidden>·</span>
              <time dateTime={article.date}>{formatDate(article.date)}</time>
              <span aria-hidden>·</span>
              <span>{article.readingMinutes} min read</span>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-3xl px-6 py-12">
          <p className="text-lg leading-relaxed text-[color:var(--ink)]">{article.intro}</p>

          {article.sections.map((s, i) => (
            <div key={i}>
              <Section s={s} />
              {i === ctaIndex && <MidArticleCta />}
            </div>
          ))}

          <Faq faqs={article.faq} />

          <p className="mt-12 rounded-lg bg-[color:var(--warm)] px-5 py-4 text-xs leading-relaxed text-[color:var(--subtle)]">
            This article is general information for Australian businesses, current at the published
            date. It is not financial, tax or legal advice. Speak to a registered agent or adviser
            about your circumstances before acting.
          </p>

          <div className="mt-12 flex items-start gap-5 card-premium p-6">
            <img
              src="/images/jamee.webp"
              alt="Jamee White, CPA, founder of NextEra Bookkeeping"
              width="72"
              height="72"
              className="h-18 w-18 rounded-xl object-cover"
              loading="lazy"
            />
            <div>
              <p className="font-serif text-lg text-[color:var(--navy)]">Jamee White, CPA</p>
              <p className="mt-1 text-sm text-[color:var(--ink)]">
                Founder of NextEra Bookkeeping. Jamee leads a team supporting established Australian
                businesses with strategic bookkeeping, reporting, payroll and Xero, and is a
                multiple national awards finalist across bookkeeping and finance.
              </p>
            </div>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="border-t border-[color:var(--mist)] bg-white">
          <div className="mx-auto max-w-6xl px-6 py-14">
            <h2 className="font-serif text-2xl text-[color:var(--navy)]">Keep reading</h2>
            <div className="mt-6 grid gap-6 md:grid-cols-3">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to="/insights/$slug"
                  params={{ slug: a.slug }}
                  className="group flex flex-col card-premium p-6"
                >
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[color:var(--brand)]">
                    {a.category}
                  </span>
                  <h3 className="mt-2 flex-1 font-serif text-lg leading-snug text-[color:var(--navy)]">
                    {a.title}
                  </h3>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[color:var(--brand)]">
                    Read article
                    <Arrow className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
